import { c as _c } from "react/compiler-runtime";
import { postPrivateAccessRequestsMutation } from '@onefootprint/fetch-dashboard';
import { getErrorMessage } from '@onefootprint/request';
import type { TenantScope } from '@onefootprint/request-types/dashboard';
import { Dialog, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { withEnvironments } from '@/components/roles/utils/scope-environments';
import useInvalidateQueries from '@/hooks/use-invalidate-queries';
import useSession from '@/hooks/use-session';
import type { GrantEditRightsFormData } from '../../types';
import GrantEditRightsForm from '../grant-edit-rights-form';
type GrantEditRightsDialogProps = {
  open: boolean;
  onClose: () => void;
  initialScopes?: string[];
};
const GrantEditRightsDialog = ({
  open,
  onClose,
  initialScopes = []
}: GrantEditRightsDialogProps) => {
  const {
    t
  } = useTranslation('common', {
    keyPrefix: 'components.privateLayout.assumeBanner.grantEditRightsDialog'
  });
  const {
    data: session
  } = useSession();
  const mutation = useMutation(postPrivateAccessRequestsMutation());
  const tenantId = session?.org?.id ?? '';
  const {
    toast
  } = useToast();
  const invalidateQueries = useInvalidateQueries();
  const handleSubmit = async (data: GrantEditRightsFormData) => {
    try {
      await mutation.mutateAsync({
        body: {
          scopes: data.scopes.map(scopeKind => withEnvironments({
            kind: scopeKind
          } as TenantScope)),
          // if this is null, we should use the placeholder of 1 day
          // footgun where we generate requests from the UI that immediately expire
          durationHours: Number((data.duration || 1) * 24),
          reason: data.reason,
          tenantId: tenantId
        }
      });
      toast({
        title: t($_0 => $_0.successToast.title),
        description: t($_1 => $_1.successToast.description)
      });
      invalidateQueries();
      onClose();
    } catch (e) {
      toast({
        title: t($ => $.errorToast.title),
        description: getErrorMessage(e)
      });
    }
  };
  return <Dialog open={open} onClose={onClose} title={t($_2 => $_2.title)} primaryButton={{
    form: 'grant-edit-rights-form',
    label: t($_3 => $_3.submit),
    type: 'submit'
  }} secondaryButton={{
    label: t($_4 => $_4.cancel),
    onClick: onClose
  }}>
      <GrantEditRightsDialogContent initialScopes={initialScopes} onSubmit={handleSubmit} />
    </Dialog>;
};
const GrantEditRightsDialogContent = t0 => {
  const $ = _c(11);
  const {
    initialScopes,
    onSubmit
  } = t0;
  let t1;
  if ($[0] !== initialScopes) {
    t1 = {
      defaultValues: {
        scopes: initialScopes,
        duration: 1,
        reason: ""
      }
    };
    $[0] = initialScopes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const methods = useForm(t1);
  let t2;
  if ($[2] !== methods || $[3] !== onSubmit) {
    t2 = methods.handleSubmit(onSubmit);
    $[2] = methods;
    $[3] = onSubmit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <GrantEditRightsForm />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <form onSubmit={t2} id="grant-edit-rights-form">{t3}</form>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== methods || $[9] !== t4) {
    t5 = <FormProvider {...methods}>{t4}</FormProvider>;
    $[8] = methods;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export default GrantEditRightsDialog;